import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  MapPin, Sun, Plane, Globe, Camera, UtensilsCrossed, 
  Mountain, Ship, ArrowRight, Heart, Star, Users
} from 'lucide-react';

export default function AboutGibraltarPage() {
  return (
    <div className="min-h-screen" data-testid="about-gibraltar-page">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-b from-[#5C0120]/10 to-white">
        <div className="container-wedding text-center">
          <span className="caption mb-4 block">DISCOVER</span>
          <h1 className="heading-xl mb-6">About Gibraltar</h1>
          <p className="text-stone-600 text-lg max-w-2xl mx-auto">
            A unique British Overseas Territory where Mediterranean charm meets British heritage,
            making it the perfect destination for your dream wedding.
          </p>
        </div>
      </section>

      {/* What is Gibraltar */}
      <section className="py-16 bg-white">
        <div className="container-wedding">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="heading-lg mb-6">The Rock of Gibraltar</h2>
                <p className="text-stone-600 mb-4 leading-relaxed">
                  Gibraltar is a British Overseas Territory located at the southern tip of the Iberian Peninsula. 
                  Known worldwide for its iconic limestone Rock, Gibraltar offers a unique blend of British and 
                  Mediterranean cultures.
                </p>
                <p className="text-stone-600 mb-4 leading-relaxed">
                  With just 6.7 square kilometers of land, this tiny territory packs in stunning scenery, 
                  rich history, diverse cuisine, and a warm, welcoming community. The territory has been 
                  British since 1713 and maintains strong ties to the UK while embracing its Mediterranean location.
                </p>
                <p className="text-stone-600 leading-relaxed">
                  Gibraltar's strategic position at the entrance to the Mediterranean Sea has made it 
                  historically significant, and today it's a thriving hub for tourism, financial services, 
                  and, of course, weddings!
                </p>
              </div>
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_278ff99f-2eb7-4de1-bb36-3367ee56d655/artifacts/h2xztvl9_250805-vanessafarokh-websized-49.jpg" 
                  alt="Newlyweds celebrating in front of the Rock of Gibraltar"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Get Married Here */}
      <section className="py-16 bg-stone-50">
        <div className="container-wedding">
          <div className="text-center mb-12">
            <span className="caption mb-3 block">WHY CHOOSE US</span>
            <h2 className="heading-lg">Why Get Married in Gibraltar?</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-6 rounded-lg border border-stone-200">
              <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-4">
                <Globe className="h-6 w-6 text-[#D4AF37]" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Internationally Recognised</h3>
              <p className="text-stone-600 text-sm">
                Gibraltar marriages are legally recognised worldwide, making paperwork simple for international couples.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-stone-200">
              <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-4">
                <Sun className="h-6 w-6 text-[#D4AF37]" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Year-Round Sunshine</h3>
              <p className="text-stone-600 text-sm">
                With over 300 days of sunshine per year, Gibraltar offers perfect weather for your special day.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-stone-200">
              <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-4">
                <Plane className="h-6 w-6 text-[#D4AF37]" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Easy to Reach</h3>
              <p className="text-stone-600 text-sm">
                Direct flights from the UK and easy access from Spain's Malaga airport make travel convenient.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-stone-200">
              <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-4">
                <Star className="h-6 w-6 text-[#D4AF37]" />
              </div>
              <h3 className="font-semibold text-lg mb-2">No Residency Required</h3>
              <p className="text-stone-600 text-sm">
                Get married with just one night's stay in Gibraltar. No lengthy residency requirements.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-stone-200">
              <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-4">
                <Camera className="h-6 w-6 text-[#D4AF37]" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Stunning Backdrops</h3>
              <p className="text-stone-600 text-sm">
                From the iconic Rock to beautiful beaches and historic venues, create unforgettable photos.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-stone-200">
              <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-4">
                <Heart className="h-6 w-6 text-[#D4AF37]" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Intimate & Personal</h3>
              <p className="text-stone-600 text-sm">
                The compact size means everything is close by, making it perfect for intimate celebrations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Things to See & Do */}
      <section className="py-16 bg-white">
        <div className="container-wedding">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <span className="caption mb-3 block">EXPLORE</span>
              <h2 className="heading-lg">Things to See & Do</h2>
              <p className="text-stone-600 mt-4">Make your wedding trip unforgettable with these Gibraltar highlights</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex gap-4 p-4 bg-stone-50 rounded-lg">
                <div className="w-12 h-12 bg-[#5C0120] rounded-full flex items-center justify-center flex-shrink-0">
                  <Mountain className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">The Upper Rock Nature Reserve</h3>
                  <p className="text-stone-600 text-sm">
                    Home to the famous Barbary macaques, St. Michael's Cave, the Moorish Castle, and breathtaking views.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 p-4 bg-stone-50 rounded-lg">
                <div className="w-12 h-12 bg-[#5C0120] rounded-full flex items-center justify-center flex-shrink-0">
                  <Ship className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Dolphin Watching</h3>
                  <p className="text-stone-600 text-sm">
                    Take a boat trip into the Strait of Gibraltar to see dolphins in their natural habitat.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 p-4 bg-stone-50 rounded-lg">
                <div className="w-12 h-12 bg-[#5C0120] rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Europa Point</h3>
                  <p className="text-stone-600 text-sm">
                    The southernmost point of Gibraltar with stunning views of Africa across the Strait.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 p-4 bg-stone-50 rounded-lg">
                <div className="w-12 h-12 bg-[#5C0120] rounded-full flex items-center justify-center flex-shrink-0">
                  <UtensilsCrossed className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Ocean Village & Queensway Quay</h3>
                  <p className="text-stone-600 text-sm">
                    Waterfront dining, shopping, and nightlife with a cosmopolitan Mediterranean atmosphere.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 p-4 bg-stone-50 rounded-lg">
                <div className="w-12 h-12 bg-[#5C0120] rounded-full flex items-center justify-center flex-shrink-0">
                  <Camera className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Sandy Bay & Catalan Bay</h3>
                  <p className="text-stone-600 text-sm">
                    Beautiful beaches perfect for relaxing, swimming, and stunning wedding photos.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 p-4 bg-stone-50 rounded-lg">
                <div className="w-12 h-12 bg-[#5C0120] rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Main Street Shopping</h3>
                  <p className="text-stone-600 text-sm">
                    Tax-free shopping on Gibraltar's bustling main street with British and international brands.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Practical Info */}
      <section className="py-16 bg-stone-50">
        <div className="container-wedding">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-lg text-center mb-10">Practical Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg border border-stone-200">
                <h3 className="font-semibold text-lg mb-4">Currency & Language</h3>
                <ul className="space-y-2 text-stone-600 text-sm">
                  <li>• <strong>Currency:</strong> Gibraltar Pound (GIP) and British Pound Sterling (GBP) accepted</li>
                  <li>• <strong>Language:</strong> English is the official language</li>
                  <li>• <strong>Spanish</strong> is widely spoken</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg border border-stone-200">
                <h3 className="font-semibold text-lg mb-4">Getting There</h3>
                <ul className="space-y-2 text-stone-600 text-sm">
                  <li>• <strong>By Air:</strong> Direct flights from UK airports</li>
                  <li>• <strong>Via Malaga:</strong> 90 minutes by car from Malaga Airport</li>
                  <li>• <strong>By Car:</strong> Drive across the border from Spain</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg border border-stone-200">
                <h3 className="font-semibold text-lg mb-4">Climate</h3>
                <ul className="space-y-2 text-stone-600 text-sm">
                  <li>• <strong>Summer:</strong> 25-30°C, perfect beach weather</li>
                  <li>• <strong>Winter:</strong> 15-18°C, mild and pleasant</li>
                  <li>• <strong>Rainfall:</strong> Mostly October to March</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg border border-stone-200">
                <h3 className="font-semibold text-lg mb-4">Entry Requirements</h3>
                <ul className="space-y-2 text-stone-600 text-sm">
                  <li>• <strong>UK Citizens:</strong> No visa required</li>
                  <li>• <strong>EU Citizens:</strong> No visa required</li>
                  <li>• <strong>Others:</strong> Contact Immigration Section</li>
                </ul>
                <div className="mt-4 pt-4 border-t border-stone-200 text-sm text-stone-600">
                  <p><strong>Immigration Section</strong></p>
                  <p>Phone: +350 20076948</p>
                  <p>Email: <a href="mailto:immigration.diha@gibraltar.gov.gi" className="text-[#5C0120] hover:underline">immigration.diha@gibraltar.gov.gi</a></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#5C0120]">
        <div className="container-wedding text-center">
          <h2 className="heading-lg text-white mb-4">Ready to Plan Your Gibraltar Wedding?</h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Discover our directory of trusted local vendors who know Gibraltar inside out.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/vendors">
              <Button className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white">
                Browse Vendors
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <Link to="/getting-married-in-gibraltar">
              <Button variant="outline" className="border-white text-white hover:bg-white/10">
                Legal Requirements
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
